// src/App.tsx
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useEffect } from 'react';
import { initializeTracking } from '@/utils/tracking';

// ==================== Admin Pages ====================
import { AdminLayout } from '@/_pages/admin/AdminLayout';
import { AdminDashboard } from '@/_pages/admin/AdminDashboard';
import { AdminProducts } from '@/_pages/admin/AdminProducts';
import { AdminCampaigns } from '@/_pages/admin/AdminCampaigns';
import { AdminAffiliates } from '@/_pages/admin/AdminAffiliates';
import { AdminLeads } from '@/_pages/admin/AdminLeads';
import { AdminPurchases } from '@/_pages/admin/AdminPurchases';
import { AdminCommissions } from '@/_pages/admin/AdminCommissions';
import { AdminSuspicious } from '@/_pages/admin/AdminSuspicious';
import { AdminAnnouncements } from '@/_pages/admin/AdminAnnouncements';
import { AdminRanking } from '@/_pages/admin/AdminRanking';
import { AdminCsvExport } from '@/_pages/admin/AdminCsvExport';
import { AdminLogin } from '@/_pages/admin/AdminLogin';
import { AdminRoles } from '@/_pages/admin/AdminRoles';
import { AdminApprovals } from '@/_pages/admin/AdminApprovals';
import { AdminCampaignAccess } from '@/_pages/admin/AdminCampaignAccess';
// 新規追加
import { AdminAffiliateRegistrations } from '@/_pages/admin/AdminAffiliateRegistrations';
import { AdminPermissions } from '@/_pages/admin/AdminPermissions';

// ==================== Affiliate Pages ====================
import { AffiliateLayout } from '@/_pages/affiliate/AffiliateLayout';
import { AffiliateDashboard } from '@/_pages/affiliate/AffiliateDashboard';
import { AffiliateDashboardNew } from '@/_pages/affiliate/AffiliateDashboardNew';
import { AffiliateLogin } from '@/_pages/affiliate/AffiliateLogin';
import { AffiliateProfile } from '@/_pages/affiliate/AffiliateProfile';
import { AffiliateMaterials } from '@/_pages/affiliate/AffiliateMaterials';
// 新規追加
import { AffiliateRegister } from '@/_pages/affiliate/AffiliateRegister';
import { AffiliateTop } from '@/_pages/affiliate/AffiliateTop';
import { AffiliateProductDetail } from '@/_pages/affiliate/AffiliateProductDetail';

// ==================== Partner Pages ====================
import { PartnerLogin } from '@/_pages/partner/PartnerLogin';
import { PartnerLayout } from '@/_pages/partner/PartnerLayout';
import { PartnerDashboard } from '@/_pages/partner/PartnerDashboard';
import { PartnerPurchases } from '@/_pages/partner/PartnerPurchases';
import { PartnerAffiliates } from '@/_pages/partner/PartnerAffiliates';
import { PartnerRequests } from '@/_pages/partner/PartnerRequests';
import { PartnerCsvExport } from '@/_pages/partner/PartnerCsvExport';
import { PartnerNotices } from '@/_pages/partner/PartnerNotices';

// ==================== LP / Sales Pages ====================
// 販売ページ（新規追加）
import { LandingPageAffiliateCourse } from '@/_pages/lp/LandingPageAffiliateCourse';
import { CushionLP } from '@/_pages/lp/CushionLP';
import { FreeNoteLP } from '@/_pages/lp/FreeNoteLP';
// 既存ページ（start-course として流用）
import { LandingPageStartCourse } from '@/_pages/lp/LandingPageStartCourse';
import { LandingPageFreeGift } from '@/_pages/lp/LandingPageFreeGift';
import { LiffSeminar } from '@/_pages/lp/LiffSeminar';
import { PurchaseComplete } from '@/_pages/lp/PurchaseComplete';
import { Contact } from '@/_pages/lp/Contact';
import { Tokushoho } from '@/_pages/lp/Tokushoho';
import { Privacy } from '@/_pages/lp/Privacy';

// ==================== Practice Log Pages ====================
import { PracticeLogAuthProvider } from '@/hooks/practice-log/useAuth';
import { AuthGuard } from '@/components/practice-log/AuthGuard';
import { PracticeLogLogin } from '@/_pages/practice-log/LoginPage';
import { DashboardPage } from '@/_pages/practice-log/DashboardPage';
import { CheckinPage } from '@/_pages/practice-log/CheckinPage';
import { CalendarPage } from '@/_pages/practice-log/CalendarPage';
import { HistoryPage } from '@/_pages/practice-log/HistoryPage';
import { AchievementsPage } from '@/_pages/practice-log/AchievementsPage';
import { BadgesPage } from '@/_pages/practice-log/BadgesPage';
import { AdminDashboard as PracticeAdminDashboard } from '@/_pages/practice-log/admin/AdminDashboard';
import { AdminMembers } from '@/_pages/practice-log/admin/AdminMembers';
import { AdminMemberDetail } from '@/_pages/practice-log/admin/AdminMemberDetail';
import { AdminQuestions } from '@/_pages/practice-log/admin/AdminQuestions';
import { AdminUnreported } from '@/_pages/practice-log/admin/AdminUnreported';
import { AdminEncourage } from '@/_pages/practice-log/admin/AdminEncourage';
import { AdminStuck } from '@/_pages/practice-log/admin/AdminStuck';
import { AdminAchievements as PracticeAdminAchievements } from '@/_pages/practice-log/admin/AdminAchievements';

// ==================== Main Site Pages ====================
import { HomePage } from '@/_pages/main/HomePage';
import { CoursesPage } from '@/_pages/main/CoursesPage';
import { CourseStartPage } from '@/_pages/main/CourseStartPage';
import { CourseAffiliatePage } from '@/_pages/main/CourseAffiliatePage';
import { ContactPage } from '@/_pages/main/ContactPage';
import { ConceptPage } from '@/_pages/main/ConceptPage';
import { ProfilePage } from '@/_pages/main/ProfilePage';
import { TokushohoPage } from '@/_pages/legal/TokushohoPage';
import { PrivacyPage } from '@/_pages/legal/PrivacyPage';
import { TermsPage } from '@/_pages/legal/TermsPage';
import { DisclaimerPage } from '@/_pages/legal/DisclaimerPage';

function App() {
  useEffect(() => {
    initializeTracking();
  }, []);

  return (
    <BrowserRouter>
      <Routes>
        {/* ========================================
            会社HP風 メインサイト（mio-mainsite.netlify.app）
           ======================================== */}
        <Route path="/" element={<HomePage />} />
        <Route path="/concept" element={<ConceptPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/courses" element={<CoursesPage />} />
        <Route path="/courses/ai-1hour-start" element={<CourseStartPage />} />
        <Route path="/courses/pro-ai-affiliate" element={<CourseAffiliatePage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/legal/tokushoho" element={<TokushohoPage />} />
        <Route path="/legal/privacy" element={<PrivacyPage />} />
        <Route path="/legal/terms" element={<TermsPage />} />
        <Route path="/legal/disclaimer" element={<DisclaimerPage />} />

        {/* ========================================
            販売ページ（メインの導線）
           ======================================== */}
        {/* ルート: アフィリエイト講座ページへリダイレクト（mio-affiliateサイト用パス） */}
        <Route path="/affiliate-top" element={<Navigate to="/courses" replace />} />

        {/* クッションLP（無料noteを読んだ人向け → 2択導線） */}
        <Route path="/cushion-lp" element={<CushionLP />} />

        {/* 無料note配布LP（note未読者 → LINE登録 → 本気送信 → note受け取り） */}
        <Route path="/free-note" element={<FreeNoteLP />} />

        {/* プロAIアフィリエイター養成講座：スタート講座1,000部到達までは¥49,800 */}
        <Route path="/affiliate-course" element={<LandingPageAffiliateCourse />} />

        {/* AI副業1時間化スタート講座（段階価格） */}
        <Route path="/start-course" element={<LandingPageStartCourse />} />

        {/* /mini-course は養成講座LPへリダイレクト（ミニ講座は存在しない） */}
        <Route path="/mini-course" element={<Navigate to="/affiliate-course" replace />} />
        <Route path="/free-gift" element={<LandingPageFreeGift />} />
        <Route path="/seminar" element={<LiffSeminar />} />
        <Route path="/purchase-complete" element={<PurchaseComplete />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/tokushoho" element={<Tokushoho />} />
        <Route path="/privacy" element={<Privacy />} />

        {/* ========================================
            アフィリエイター向けページ
           ======================================== */}
        {/* 自社講座のアフィリエイター新規登録は終了 */}
        <Route path="/affiliate/register" element={<Navigate to="/courses" replace />} />

        {/* アフィリエイターログイン */}
        <Route path="/affiliate/login" element={<AffiliateLogin />} />

        {/* アフィリエイターダッシュボード（認証必要） */}
        <Route path="/affiliate" element={<AffiliateLayout />}>
          <Route index element={<Navigate to="/affiliate/dashboard" replace />} />
          {/* 新版ダッシュボード: 紹介URL・権限商品のみ表示 */}
          <Route path="dashboard" element={<AffiliateDashboardNew />} />
          {/* 商品詳細ページ（紹介権限ある商品のみ表示） */}
          <Route path="products/:productId" element={<AffiliateProductDetail />} />
          <Route path="profile" element={<AffiliateProfile />} />
          <Route path="materials" element={<AffiliateMaterials />} />
        </Route>

        {/* ========================================
            管理者画面（super_admin専用）
           ======================================== */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<Navigate to="/admin/dashboard" replace />} />
          <Route path="dashboard" element={<AdminDashboard />} />

          {/* アフィリエイター管理 */}
          <Route path="affiliate-registrations" element={<AdminAffiliateRegistrations />} />
          <Route path="affiliates" element={<AdminAffiliates />} />
          <Route path="permissions" element={<AdminPermissions />} />
          <Route path="commissions" element={<AdminCommissions />} />

          {/* 商品・売上管理 */}
          <Route path="products" element={<AdminProducts />} />
          <Route path="purchases" element={<AdminPurchases />} />
          {/* クリック管理: 暫定でSuspiciousページを流用 */}
          <Route path="clicks" element={<AdminSuspicious />} />

          {/* パートナー管理 */}
          <Route path="roles" element={<AdminRoles />} />
          <Route path="approvals" element={<AdminApprovals />} />
          <Route path="campaign-access" element={<AdminCampaignAccess />} />

          {/* その他 */}
          <Route path="campaigns" element={<AdminCampaigns />} />
          <Route path="leads" element={<AdminLeads />} />
          <Route path="announcements" element={<AdminAnnouncements />} />
          <Route path="ranking" element={<AdminRanking />} />
          <Route path="csv" element={<AdminCsvExport />} />
          <Route path="suspicious" element={<AdminSuspicious />} />
        </Route>

        {/* ========================================
            パートナー管理画面（product_owner専用）
           ======================================== */}
        <Route path="/partner/login" element={<PartnerLogin />} />
        <Route path="/partner" element={<PartnerLayout />}>
          <Route index element={<Navigate to="/partner/dashboard" replace />} />
          <Route path="dashboard" element={<PartnerDashboard />} />
          <Route path="purchases" element={<PartnerPurchases />} />
          <Route path="affiliates" element={<PartnerAffiliates />} />
          <Route path="requests" element={<PartnerRequests />} />
          <Route path="csv" element={<PartnerCsvExport />} />
          <Route path="notices" element={<PartnerNotices />} />
        </Route>

        {/* ========================================
            実践ログアプリ（みお革命 実践ログ）
           ======================================== */}
        <Route path="/practice-log/*" element={
          <PracticeLogAuthProvider>
            <Routes>
              {/* ログイン（認証不要） */}
              <Route path="login" element={<PracticeLogLogin />} />

              {/* メンバー向けページ（要ログイン） */}
              <Route path="dashboard" element={<AuthGuard><DashboardPage /></AuthGuard>} />
              <Route path="checkin" element={<AuthGuard><CheckinPage /></AuthGuard>} />
              <Route path="calendar" element={<AuthGuard><CalendarPage /></AuthGuard>} />
              <Route path="history" element={<AuthGuard><HistoryPage /></AuthGuard>} />
              <Route path="achievements" element={<AuthGuard><AchievementsPage /></AuthGuard>} />
              <Route path="badges" element={<AuthGuard><BadgesPage /></AuthGuard>} />

              {/* staff以上が見られる画面（閲覧・分析・質問対応） */}
              <Route path="admin" element={<AuthGuard requiredRole="staff"><PracticeAdminDashboard /></AuthGuard>} />
              <Route path="admin/members" element={<AuthGuard requiredRole="staff"><AdminMembers /></AuthGuard>} />
              <Route path="admin/members/:id" element={<AuthGuard requiredRole="staff"><AdminMemberDetail /></AuthGuard>} />
              <Route path="admin/questions" element={<AuthGuard requiredRole="staff"><AdminQuestions /></AuthGuard>} />
              <Route path="admin/unreported" element={<AuthGuard requiredRole="staff"><AdminUnreported /></AuthGuard>} />
              <Route path="admin/encourage" element={<AuthGuard requiredRole="staff"><AdminEncourage /></AuthGuard>} />
              <Route path="admin/stuck" element={<AuthGuard requiredRole="staff"><AdminStuck /></AuthGuard>} />
              <Route path="admin/achievements" element={<AuthGuard requiredRole="staff"><PracticeAdminAchievements /></AuthGuard>} />

              {/* デフォルト: ダッシュボードへ */}
              <Route path="*" element={<Navigate to="/practice-log/dashboard" replace />} />
            </Routes>
          </PracticeLogAuthProvider>
        } />

        {/* 404 → トップへ */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
